import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  MarkEmailRead as MarkEmailReadIcon,
  MarkEmailUnread as MarkEmailUnreadIcon,
  ForwardToInbox as ForwardIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  ReportProblem as ReportProblemIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  PendingActions as PendingActionsIcon,
  Block as BlockIcon,
  Send as SendIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axios from '../utils/axios';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'warning', icon: <HourglassEmptyIcon fontSize="small" /> },
  read: { label: 'Read', color: 'info', icon: <MarkEmailReadIcon fontSize="small" /> },
  'under-review': { label: 'Under Review', color: 'secondary', icon: <PendingActionsIcon fontSize="small" /> },
  'action-taken': { label: 'Action Taken', color: 'primary', icon: <CheckCircleIcon fontSize="small" /> },
  resolved: { label: 'Resolved', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
  dismissed: { label: 'Dismissed', color: 'default', icon: <BlockIcon fontSize="small" /> },
  archived: { label: 'Archived', color: 'default', icon: <ArchiveIcon fontSize="small" /> }
};

const SEVERITY_CONFIG = {
  low: { label: 'Low', color: 'default' },
  medium: { label: 'Medium', color: 'warning' },
  high: { label: 'High', color: 'error' },
  critical: { label: 'Critical', color: 'error' }
};

const REPORT_TYPE_CONFIG = {
  user: { label: 'User Report', color: 'primary' },
  content: { label: 'Content', color: 'secondary' },
  bug: { label: 'Bug', color: 'warning' },
  other: { label: 'Other', color: 'default' }
};

const ReportsManagement = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingReportId, setUpdatingReportId] = useState(null);
  const [stats, setStats] = useState({
    pending: 0,
    read: 0,
    'under-review': 0,
    'action-taken': 0,
    dismissed: 0,
    resolved: 0,
    archived: 0
  });

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  // Pagination
  const [page, setPage] = useState(0); // MUI 0-indexed
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Dialog States
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  const [forwardDialogOpen, setForwardDialogOpen] = useState(false);
  const [reportToForward, setReportToForward] = useState(null);
  const [forwardEmail, setForwardEmail] = useState('');
  const [forwardNote, setForwardNote] = useState('');
  const [forwarding, setForwarding] = useState(false);

  // Fetch reports
  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage
      };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (severityFilter !== 'all') params.severity = severityFilter;
      if (typeFilter !== 'all') params.reportType = typeFilter;

      const response = await axios.get('/report/admin/all', { params });
      if (response.data && response.data.success) {
        setReports(response.data.data || []);
        if (response.data.stats) {
          setStats(response.data.stats);
        }
        if (response.data.pagination) {
          setTotalCount(response.data.pagination.totalItems || 0);
        }
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error(error.response?.data?.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, severityFilter, typeFilter]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Handle filter changes
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(0);
  };

  const handleSeverityFilterChange = (e) => {
    setSeverityFilter(e.target.value);
    setPage(0);
  };

  const handleTypeFilterChange = (e) => {
    setTypeFilter(e.target.value);
    setPage(0);
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setSeverityFilter('all');
    setTypeFilter('all');
    setPage(0);
  };

  // Pagination Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter client-side by search term (search within concerns or email or reporter name)
  const displayedReports = reports.filter((item) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const reporterName = item.reportedBy?.name?.toLowerCase() || '';
    const email = item.email?.toLowerCase() || '';
    const concerns = item.concerns?.toLowerCase() || '';
    const reportedUserName = item.reportedUser?.name?.toLowerCase() || '';
    return (
      reporterName.includes(term) ||
      email.includes(term) ||
      concerns.includes(term) ||
      reportedUserName.includes(term)
    );
  });

  // Action: View
  const handleOpenView = (report) => {
    setSelectedReport(report);
    setViewDialogOpen(true);
  };

  const handleCloseView = () => {
    setViewDialogOpen(false);
    setSelectedReport(null);
  };

  // Action: Archive / Unarchive
  const handleArchive = async (report) => {
    if (!report?._id) return;
    const isCurrentlyArchived = report.status === 'archived';
    const newStatus = isCurrentlyArchived ? 'pending' : 'archived';

    try {
      setUpdatingReportId(report._id);
      const response = await axios.put(`/report/${report._id}/status`, { status: newStatus });
      if (response.data && response.data.success) {
        toast.success(isCurrentlyArchived ? 'Report unarchived' : 'Report archived successfully');
        setReports((prev) =>
          prev.map((r) => (r._id === report._id ? { ...r, status: newStatus } : r))
        );
        if (selectedReport && selectedReport._id === report._id) {
          setSelectedReport((prev) => ({ ...prev, status: newStatus }));
        }
        fetchReports();
      }
    } catch (error) {
      console.error('Error archiving report:', error);
      toast.error(error.response?.data?.message || 'Failed to update archive status');
    } finally {
      setUpdatingReportId(null);
    }
  };

  // Action: Mark as Read / Unread
  const handleMarkAsRead = async (report) => {
    if (!report?._id) return;
    const isCurrentlyRead = report.status === 'read';
    const newStatus = isCurrentlyRead ? 'pending' : 'read';

    try {
      setUpdatingReportId(report._id);
      const response = await axios.put(`/report/${report._id}/status`, { status: newStatus });
      if (response.data && response.data.success) {
        toast.success(isCurrentlyRead ? 'Report marked as unread' : 'Report marked as read');
        setReports((prev) =>
          prev.map((r) => (r._id === report._id ? { ...r, status: newStatus } : r))
        );
        if (selectedReport && selectedReport._id === report._id) {
          setSelectedReport((prev) => ({ ...prev, status: newStatus }));
        }
        fetchReports();
      }
    } catch (error) {
      console.error('Error marking report as read:', error);
      toast.error(error.response?.data?.message || 'Failed to mark report as read');
    } finally {
      setUpdatingReportId(null);
    }
  };

  // Action: Forward
  const handleOpenForward = (report) => {
    setReportToForward(report);
    setForwardEmail('');
    setForwardNote('');
    setForwardDialogOpen(true);
  };

  const handleCloseForward = () => {
    setForwardDialogOpen(false);
    setReportToForward(null);
    setForwardEmail('');
    setForwardNote('');
  };

  const handleSendForward = () => {
    if (!forwardEmail || !/^\S+@\S+\.\S+$/.test(forwardEmail)) {
      toast.error('Please enter a valid recipient email address');
      return;
    }

    setForwarding(true);
    setTimeout(() => {
      setForwarding(false);
      toast.success(`Report forwarded to ${forwardEmail}`);
      handleCloseForward();
    }, 500);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Reports & Requests
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review user-submitted reports, content violations, issues, and requests.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchReports}
          disabled={loading}
          sx={{ borderRadius: 2 }}
        >
          Refresh
        </Button>
      </Box>

      {/* Stats Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
            lg: 'repeat(7, 1fr)'
          },
          gap: 2,
          mb: 3
        }}
      >
        {[
          {
            label: 'Pending',
            value: stats['pending'] || 0,
            subtitle: 'Awaiting triage',
            icon: <HourglassEmptyIcon sx={{ fontSize: 20 }} />,
            iconBg: '#FEF3C7',
            iconColor: '#D97706',
          },
          {
            label: 'Read',
            value: stats['read'] || 0,
            subtitle: 'Reviewed / Opened',
            icon: <MarkEmailReadIcon sx={{ fontSize: 20 }} />,
            iconBg: '#E0F2FE',
            iconColor: '#0284C7',
          },
          {
            label: 'Under Review',
            value: stats['under-review'] || 0,
            subtitle: 'In investigation',
            icon: <PendingActionsIcon sx={{ fontSize: 20 }} />,
            iconBg: '#EDE9FE',
            iconColor: '#4F46E5',
          },
          {
            label: 'Action Taken',
            value: stats['action-taken'] || 0,
            subtitle: 'Measures applied',
            icon: <ReportProblemIcon sx={{ fontSize: 20 }} />,
            iconBg: '#EDE9FE',
            iconColor: '#7C3AED',
          },
          {
            label: 'Resolved',
            value: stats['resolved'] || 0,
            subtitle: 'Completed',
            icon: <CheckCircleIcon sx={{ fontSize: 20 }} />,
            iconBg: '#F0FDF4',
            iconColor: '#16A34A',
          },
          {
            label: 'Dismissed',
            value: stats['dismissed'] || 0,
            subtitle: 'Closed / Rejected',
            icon: <BlockIcon sx={{ fontSize: 20 }} />,
            iconBg: '#F3F4F6',
            iconColor: '#6B7280',
          },
          {
            label: 'Archived',
            value: stats['archived'] || 0,
            subtitle: 'Stored away',
            icon: <ArchiveIcon sx={{ fontSize: 20 }} />,
            iconBg: '#F1F5F9',
            iconColor: '#475569',
          },
        ].map((stat) => (
          <Paper
            key={stat.label}
            elevation={0}
            sx={{
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              p: 2,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              backgroundColor: '#ffffff',
              height: '100%',
            }}
          >
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: '#6B7280',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  mb: 0.5,
                }}
              >
                {stat.label}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '1.5rem',
                  color: '#111827',
                  lineHeight: 1.2,
                  mb: 0.5,
                }}
              >
                {stat.value}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: '#9CA3AF',
                  fontSize: '0.72rem',
                }}
              >
                {stat.subtitle}
              </Typography>
            </Box>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '8px',
                bgcolor: stat.iconBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: stat.iconColor,
                flexShrink: 0,
              }}
            >
              {stat.icon}
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Filter and Search Bar */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2.5, boxShadow: 1 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by concerns, email or reporter..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4} md={2.3}>
            <FormControl fullWidth size="small">
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                id="status-filter"
                value={statusFilter}
                label="Status"
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="read">Read</MenuItem>
                <MenuItem value="under-review">Under Review</MenuItem>
                <MenuItem value="action-taken">Action Taken</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="dismissed">Dismissed</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4} md={2.3}>
            <FormControl fullWidth size="small">
              <InputLabel id="severity-filter-label">Severity</InputLabel>
              <Select
                labelId="severity-filter-label"
                id="severity-filter"
                value={severityFilter}
                label="Severity"
                onChange={handleSeverityFilterChange}
              >
                <MenuItem value="all">All Severities</MenuItem>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4} md={2.3}>
            <FormControl fullWidth size="small">
              <InputLabel id="type-filter-label">Report Type</InputLabel>
              <Select
                labelId="type-filter-label"
                id="type-filter"
                value={typeFilter}
                label="Report Type"
                onChange={handleTypeFilterChange}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="user">User Report</MenuItem>
                <MenuItem value="content">Content</MenuItem>
                <MenuItem value="bug">Bug</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={1.1}>
            <Button
              fullWidth
              size="small"
              variant="text"
              startIcon={<FilterListIcon />}
              onClick={handleResetFilters}
              sx={{ height: 40, color: 'text.secondary' }}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Reports Table */}
      <Paper sx={{ borderRadius: 2.5, overflow: 'hidden', boxShadow: 1 }}>
        <TableContainer>
          <Table sx={{ minWidth: 850 }}>
            <TableHead sx={{ backgroundColor: 'grey.50' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Reported By</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Contact Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Concerns</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Severity</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={36} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Loading reports...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : displayedReports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    <ReportProblemIcon sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      No reports found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      No user reports match the selected filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayedReports.map((report, index) => {
                  const statusInfo = STATUS_CONFIG[report.status] || {
                    label: report.status,
                    color: 'default'
                  };
                  const severityInfo = SEVERITY_CONFIG[report.severity] || {
                    label: report.severity,
                    color: 'default'
                  };
                  const typeInfo = REPORT_TYPE_CONFIG[report.reportType] || {
                    label: report.reportType,
                    color: 'default'
                  };

                  return (
                    <TableRow key={report._id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                        {page * rowsPerPage + index + 1}
                      </TableCell>

                      {/* Reported By */}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {report.reportedBy?.name || 'Anonymous User'}
                        </Typography>
                        {report.reportedUser && (
                          <Typography variant="caption" color="error.main" display="block">
                            Reported: {report.reportedUser.name || 'User'}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Email */}
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {report.email}
                        </Typography>
                      </TableCell>

                      {/* Type */}
                      <TableCell>
                        <Chip
                          label={typeInfo.label}
                          size="small"
                          color={typeInfo.color}
                          variant="outlined"
                          sx={{ textTransform: 'capitalize', fontWeight: 500 }}
                        />
                      </TableCell>

                      {/* Concerns */}
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Tooltip title={report.concerns} placement="top" arrow>
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              cursor: 'pointer'
                            }}
                          >
                            {report.concerns}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Chip
                          label={statusInfo.label}
                          size="small"
                          color={statusInfo.color}
                          sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                        />
                      </TableCell>

                      {/* Severity */}
                      <TableCell>
                        <Chip
                          label={severityInfo.label}
                          size="small"
                          color={severityInfo.color}
                          variant="outlined"
                          sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                        />
                      </TableCell>

                      {/* Date */}
                      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.85rem', color: 'text.secondary' }}>
                        {report.createdAt
                          ? new Date(report.createdAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })
                          : 'N/A'}
                      </TableCell>

                      {/* Actions: View, Archive, Mark as Read, Forward */}
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          {/* View */}
                          <Tooltip title="View Report">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenView(report)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {/* Archive */}
                          <Tooltip title={report.status === 'archived' ? 'Unarchive Report' : 'Archive Report'}>
                            <span>
                              <IconButton
                                size="small"
                                color={report.status === 'archived' ? 'warning' : 'default'}
                                onClick={() => handleArchive(report)}
                                disabled={updatingReportId === report._id}
                              >
                                {updatingReportId === report._id ? (
                                  <CircularProgress size={16} />
                                ) : report.status === 'archived' ? (
                                  <UnarchiveIcon fontSize="small" />
                                ) : (
                                  <ArchiveIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>

                          {/* Mark as read */}
                          <Tooltip title={report.status === 'read' ? 'Mark as Unread' : 'Mark as Read'}>
                            <span>
                              <IconButton
                                size="small"
                                color={report.status === 'read' ? 'success' : 'info'}
                                onClick={() => handleMarkAsRead(report)}
                                disabled={updatingReportId === report._id}
                              >
                                {updatingReportId === report._id ? (
                                  <CircularProgress size={16} />
                                ) : report.status === 'read' ? (
                                  <MarkEmailUnreadIcon fontSize="small" />
                                ) : (
                                  <MarkEmailReadIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>

                          {/* Forward */}
                          <Tooltip title="Forward">
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleOpenForward(report)}
                            >
                              <ForwardIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* VIEW MODAL DIALOG */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseView}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Report Details
            </Typography>
            {selectedReport && (
              <Typography variant="caption" color="text.secondary">
                Report ID: {selectedReport._id}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={handleCloseView}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />

        <DialogContent sx={{ pt: 2.5 }}>
          {selectedReport && (
            <Stack spacing={2.5}>
              {/* Status & Severity Banner */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={`Status: ${STATUS_CONFIG[selectedReport.status]?.label || selectedReport.status}`}
                  color={STATUS_CONFIG[selectedReport.status]?.color || 'default'}
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  label={`Severity: ${SEVERITY_CONFIG[selectedReport.severity]?.label || selectedReport.severity}`}
                  color={SEVERITY_CONFIG[selectedReport.severity]?.color || 'default'}
                  variant="outlined"
                  sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                />
                <Chip
                  label={`Type: ${REPORT_TYPE_CONFIG[selectedReport.reportType]?.label || selectedReport.reportType}`}
                  color={REPORT_TYPE_CONFIG[selectedReport.reportType]?.color || 'default'}
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  label={`Created: ${new Date(selectedReport.createdAt).toLocaleString()}`}
                  variant="outlined"
                  size="small"
                  sx={{ ml: 'auto' }}
                />
              </Box>

              {/* Reporters and Target Grid */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, backgroundColor: 'grey.50' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      REPORTED BY
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedReport.reportedBy?.name || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      User Email: {selectedReport.reportedBy?.email || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Contact Email: {selectedReport.email}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      REPORTED TARGET / USER
                    </Typography>
                    {selectedReport.reportedUser ? (
                      <>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'error.main' }}>
                          {selectedReport.reportedUser.name || 'User'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Target Email: {selectedReport.reportedUser.email || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Target User ID: {selectedReport.reportedUser._id || selectedReport.reportedUser}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        No specific user reported (General report)
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Paper>

              {/* Concerns */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Concerns / Description
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: 'background.paper',
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.6
                  }}
                >
                  <Typography variant="body2">{selectedReport.concerns}</Typography>
                </Paper>
              </Box>

              {/* Action Taken if any */}
              {selectedReport.actionTaken && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Action Taken
                  </Typography>
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    {selectedReport.actionTaken}
                  </Alert>
                </Box>
              )}

              {/* Admin Notes */}
              {selectedReport.adminNotes && selectedReport.adminNotes.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Admin Notes ({selectedReport.adminNotes.length})
                  </Typography>
                  <Stack spacing={1}>
                    {selectedReport.adminNotes.map((noteItem, idx) => (
                      <Paper key={idx} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                        <Typography variant="body2">{noteItem.note}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          By {noteItem.addedBy?.name || 'Admin'} on{' '}
                          {new Date(noteItem.addedAt).toLocaleString()}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>

        <Divider />
        <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={handleCloseView} variant="outlined" sx={{ borderRadius: 2 }}>
            Close
          </Button>
          {selectedReport && (
            <>
              <Button
                onClick={() => handleMarkAsRead(selectedReport)}
                variant="outlined"
                color={selectedReport.status === 'read' ? 'inherit' : 'info'}
                startIcon={
                  updatingReportId === selectedReport._id ? (
                    <CircularProgress size={16} />
                  ) : selectedReport.status === 'read' ? (
                    <MarkEmailUnreadIcon />
                  ) : (
                    <MarkEmailReadIcon />
                  )
                }
                disabled={updatingReportId === selectedReport._id}
                sx={{ borderRadius: 2 }}
              >
                {selectedReport.status === 'read' ? 'Mark as Unread' : 'Mark as Read'}
              </Button>
              <Button
                onClick={() => handleArchive(selectedReport)}
                variant="outlined"
                color={selectedReport.status === 'archived' ? 'inherit' : 'warning'}
                startIcon={
                  updatingReportId === selectedReport._id ? (
                    <CircularProgress size={16} />
                  ) : selectedReport.status === 'archived' ? (
                    <UnarchiveIcon />
                  ) : (
                    <ArchiveIcon />
                  )
                }
                disabled={updatingReportId === selectedReport._id}
                sx={{ borderRadius: 2 }}
              >
                {selectedReport.status === 'archived' ? 'Unarchive' : 'Archive'}
              </Button>
            </>
          )}
          <Button
            onClick={() => {
              handleCloseView();
              if (selectedReport) handleOpenForward(selectedReport);
            }}
            variant="contained"
            startIcon={<ForwardIcon />}
            sx={{ borderRadius: 2 }}
          >
            Forward Report
          </Button>
        </DialogActions>
      </Dialog>

      {/* FORWARD MODAL DIALOG */}
      <Dialog
        open={forwardDialogOpen}
        onClose={handleCloseForward}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Forward Report
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Send this report details to a team member or external party.
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleCloseForward}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />

        <DialogContent sx={{ pt: 2.5 }}>
          {reportToForward && (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, backgroundColor: 'grey.50' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  REPORT SUMMARY
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                  Type: {REPORT_TYPE_CONFIG[reportToForward.reportType]?.label || reportToForward.reportType} | Severity: {reportToForward.severity}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  "{reportToForward.concerns}"
                </Typography>
              </Paper>

              <TextField
                fullWidth
                label="Recipient Email"
                type="email"
                required
                placeholder="colleague@example.com"
                value={forwardEmail}
                onChange={(e) => setForwardEmail(e.target.value)}
              />

              <TextField
                fullWidth
                label="Additional Notes (Optional)"
                multiline
                rows={3}
                placeholder="Add context or instructions for the recipient..."
                value={forwardNote}
                onChange={(e) => setForwardNote(e.target.value)}
              />
            </Stack>
          )}
        </DialogContent>

        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseForward} variant="outlined" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSendForward}
            variant="contained"
            disabled={forwarding || !forwardEmail}
            startIcon={forwarding ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
            sx={{ borderRadius: 2 }}
          >
            {forwarding ? 'Sending...' : 'Forward'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportsManagement;
